"use client";

import { Suspense } from "react";
import { ExternalLink } from "lucide-react";
import { HOWARD_COUNTY_LINKS } from "@/components/tenant/howard-county/howard-county-config";
import { useT } from "@/features/i18n/hooks/use-t";
import HcTermsContent from "./HcTermsContent";
import TilliXTermsContent from "./TilliXTermsContent";
import type { TermsSection } from "./hc-terms-data";

interface TermsContentProps {
  hcTermsPromise: Promise<TermsSection[]>;
}

function HcTermsLoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="space-y-2">
        <div className="h-5 w-2/3 rounded bg-muted" />
        <div className="h-4 w-full rounded bg-muted" />
        <div className="h-4 w-5/6 rounded bg-muted" />
      </div>
      <div className="space-y-2">
        <div className="h-5 w-1/2 rounded bg-muted" />
        <div className="h-4 w-full rounded bg-muted" />
        <div className="h-4 w-4/6 rounded bg-muted" />
      </div>
      <div className="space-y-2">
        <div className="h-5 w-3/5 rounded bg-muted" />
        <div className="h-4 w-full rounded bg-muted" />
        <div className="h-4 w-5/6 rounded bg-muted" />
      </div>
    </div>
  );
}

export default function TermsContent({ hcTermsPromise }: TermsContentProps) {
  const { t } = useT();

  return (
    <div className="w-full space-y-10 pb-10 sm:pb-0">
      <section>
        <h2 className="mb-4 font-semibold text-lg">{t("terms.tilliXTitle")}</h2>
        <TilliXTermsContent />
      </section>

      <section>
        <h2 className="mb-4 font-semibold text-lg">
          {t("terms.howardCountyTitle")}
        </h2>
        <Suspense fallback={<HcTermsLoadingSkeleton />}>
          <HcTermsContent termsPromise={hcTermsPromise} />
        </Suspense>
      </section>

      <section>
        <h2 className="mb-4 font-semibold text-lg">
          {t("terms.howardCountyStatement")}
        </h2>
        <a
          className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
          href={HOWARD_COUNTY_LINKS.termsOfUse}
          rel="noopener noreferrer"
          target="_blank"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t("terms.howardCountyLinkText")}
        </a>
      </section>
    </div>
  );
}
